import Clarity from '@microsoft/clarity'

/**
 * Microsoft Clarity Configuration for ERA Dispatch Application
 *
 * This module provides Clarity configuration for user behavior analytics
 * and session recording across all environments.
 */

// Extend Window interface to include clarity function
declare global {
  interface Window {
    clarity?: (command: string, ...args: any[]) => void
  }
}

/**
 * Initialize Microsoft Clarity with project configuration
 */
export const initializeClarity = (): void => {
  const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID

  // Don't initialize if project ID is not provided
  if (!projectId) {
    console.warn('Clarity project ID not provided - analytics disabled')
    return
  }

  const isDevelopment = import.meta.env.DEV

  try {
    // Initialize Clarity
    Clarity.init(projectId)

    if (isDevelopment) {
      console.log(`Clarity initialized with project ID: ${projectId}`)
    }
  } catch (error) {
    console.error('Failed to initialize Clarity:', error)
  }
}

/**
 * Set user identity for Clarity
 * This helps track user sessions across devices
 */
export const setClarityUser = (
  customId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string
): void => {
  try {
    Clarity.identify(customId, customSessionId, customPageId, friendlyName)
  } catch (error) {
    console.error('Failed to set Clarity user:', error)
  }
}

/**
 * Set custom tag for Clarity
 * Useful for segmenting sessions by user properties
 */
export const setClarityTag = (key: string, value: string | string[]): void => {
  try {
    Clarity.setTag(key, value)
  } catch (error) {
    console.error('Failed to set Clarity tag:', error)
  }
}

/**
 * Set GDPR consent for Clarity
 * @param consent - true to grant consent, false to revoke
 */
export const setClarityConsent = (consent: boolean = true): void => {
  try {
    Clarity.consent(consent)
  } catch (error) {
    console.error('Failed to set Clarity consent:', error)
  }
}

/**
 * Set GDPR consent v2 for Clarity with granular controls
 */
export const setClarityConsentV2 = (consentOptions?: {
  ad_Storage: 'granted' | 'denied'
  analytics_Storage: 'granted' | 'denied'
}): void => {
  try {
    Clarity.consentV2(consentOptions)
  } catch (error) {
    console.error('Failed to set Clarity consent v2:', error)
  }
}

/**
 * Upgrade the current session to be recorded
 * Useful for capturing sessions of specific user actions
 */
export const upgradeClaritySession = (reason: string): void => {
  try {
    Clarity.upgrade(reason)
  } catch (error) {
    console.error('Failed to upgrade Clarity session:', error)
  }
}

/**
 * Add custom event to Clarity
 */
export const sendClarityEvent = (eventName: string): void => {
  try {
    Clarity.event(eventName)
  } catch (error) {
    console.error('Failed to send Clarity event:', error)
  }
}

// Export Clarity instance for direct usage when needed
export { Clarity }
